import { freeze } from 'immer';
import { get } from '../../lib/util';
import type { Events, TimeData } from './TimeData';
import type { LoadedCompartment } from './Core.cargo-allocation';
import type { LocationId } from '@seaber/common/core/entries';
import type { AssortmentGroupId, AssortmentId, OrderId } from '../../graphql';

export type DeriveCapacitiesAndTimesState = {
  prev: LocationId;
  /** Index of the PortCallArrivalEvent of the current port call */
  portCallArrivalIndex: number | null;
  arriveInPort: number;
  previousEnd: number;
  volumeCapacityAvailable: number;
  massCapacityAvailable: number;
  deckVolumeCapacityAvailable: number;
  loadedCompartmentsById: Map<number, LoadedCompartment>;
  totalHoursAtSea: number;
  totalHoursAtPort: number;
  ballastDistance: number;
  totalDistance: number;
  bunkerCost: number;
  portCostEstimateTotal: number;
  totalMass: number;
  delays: number;
  constrainedDelays: number;
  tcCostTotal: number;
  cost: number;
  orderToLoadIndex: Map<number, number>;
  hasFirstShipmentEvent: boolean;
  concurrentPortHours: number;
  currentPortCallOperationsStart: number | null;
  ordersOnBoard: number;
  /** Tracks which orders/assortments are currently on board */
  carriedAssortmentsByOrderId: Map<
    OrderId,
    { assortmentId: AssortmentId; assortmentGroupId: AssortmentGroupId | null }
  >;
};

/** Clones the state object.
 * cloneDeep is too costly because of large compartment and assortment objects */
export const cloneDeriveCapacitiesAndTimesState = (
  state: DeriveCapacitiesAndTimesState,
): DeriveCapacitiesAndTimesState => {
  const res = {
    ...state,
    carriedAssortmentsByOrderId: new Map(state.carriedAssortmentsByOrderId),
    loadedCompartmentsById: new Map(
      [...state.loadedCompartmentsById.entries()].map(([k, v]) => [
        k,
        { ...v, allocations: [...v.allocations] },
      ]),
    ),
    orderToLoadIndex: new Map(state.orderToLoadIndex),
  };
  return res;
};

/** A symbol that is used to represent a null value in the cache.
 * We can't use null itself because it is not a valid WeakMap key. */
export type NullSymbol = symbol & { __brand: 'NULL' };
export const NULL_SYMBOL = Symbol('NULL') as NullSymbol;

const cachedTimeData = new WeakMap<
  Events,
  WeakMap<
    TimeData | NullSymbol,
    readonly [TimeData, DeriveCapacitiesAndTimesState]
  >
>();

const readCache = <T extends Events>(
  e: Events,
  prev: TimeData<T> | null,
): [TimeData<T>, DeriveCapacitiesAndTimesState] | null => {
  return (cachedTimeData.get(e)?.get(prev ?? NULL_SYMBOL) ?? null) as
    | [TimeData<T>, DeriveCapacitiesAndTimesState]
    | null;
};

export const cacheTimeData = <T extends Events>(
  e: Events,
  prev: TimeData<T> | null,
  state: [TimeData<T>, DeriveCapacitiesAndTimesState],
) => {
  // freeze ensures that we don't have any bugs which would mutate a cached value
  const value = [state[0], freeze(state[1], true)] as const;
  if (!cachedTimeData.has(e)) {
    cachedTimeData.set(e, new WeakMap());
  }
  cachedTimeData.get(e)?.set(prev ?? NULL_SYMBOL, value);
};

/** Constructs initial part of derived timeline from cache.
 * Returns the index where the cache ends, the state object and the timedata array filled up to startIndex */
export function getCachedTimeData<EventType extends Events>(
  events: EventType[],
): {
  startIndex: number;
  state: DeriveCapacitiesAndTimesState | null;
  timeData: TimeData<EventType>[];
} {
  let lastCachedState: DeriveCapacitiesAndTimesState | null = null;
  let startIndex = 0;
  const arr = new Array<TimeData<EventType>>(events.length);
  for (; startIndex < events.length; startIndex++) {
    const event = events[startIndex];
    const prevTd = get(arr, startIndex - 1);
    const cachedResult = readCache(event, prevTd ?? null);
    if (cachedResult) {
      arr[startIndex] = cachedResult[0];
      lastCachedState = cachedResult[1];
    } else {
      break;
    }
  }
  return {
    startIndex,
    state:
      lastCachedState && cloneDeriveCapacitiesAndTimesState(lastCachedState),
    timeData: arr,
  };
}
